import React, { Suspense, useCallback, useContext, useEffect, useRef, lazy } from "react";
import { Route, Switch, useLocation, useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import * as Sentry from "@sentry/react";

import { PrivateRoute, ROUTES_WHITElIST } from "utils/routes";
import { AuthContext } from "features/Auth";
// import mixpanel from "mixpanel-browser";

import Loading from "components/Loading";
import ErrorFallback from "components/ErrorFallback";
import Dashboard from "features/Dashboard/container";
import Login from "features/Auth/Login/container";
import Register from "features/Auth/Register/container";
import Create from "features/CreateModel";
import Model from "features/Model";
import CodeLibrary from "features/CodeLibrary";
import UpgradeLibrary from "features/UpgradeLibrary";
import ReportGenerator from "features/ReportGenerator/container";
import LegacyParametricAnalysis from "features/LegacyParametricAnalysis/container";
import CommunitiesDashboard from "features/Dashboards/CommunitiesDashboard";
import Community from "features/Community";
import QuickStart from "features/Resources/QuickStart";
import FAQs from "features/Resources/FAQs";
import ContactUs from "features/Resources/ContactUs";
import Roadmap from "features/Resources/Roadmap";
import TermsAndConditions from "features/Resources/TermsAndConditions";
import PrivacyPolicy from "features/Resources/PrivacyPolicy";
import SoftwareLicence from "features/Resources/SoftwareLicence";
import Updates from "features/Resources/Updates";
import EmissionFactors from "features/Resources/EmissionFactors";
import OperatingConditions from "features/Resources/OperatingConditions";
import ReferenceHouses from "features/Resources/ReferenceHouses";
import Preferences from "features/User/Preferences";
import Account from "features/User/Account/container";
import Organization from "features/User/Organization";
import PageNotFound from "features/Navigation/PageNotFound";
import ParametricAnalysis from "features/ParametricAnalysis";
import PasswordReset from "features/Auth/PasswordReset";
import NewPassword from "features/Auth/NewPassword";
import Transactions from "features/Transactions";
import ActionRedirect from "features/ActionRedirect";
import Insights from "features/Insights";
import Pricing from "features/Pricing";

// const Dashboard = lazy(() => import("features/Dashboard/container"));
// const Login = lazy(() => import("features/Auth/Login/container"));
// const Register = lazy(() => import("features/Auth/Register/container"));
// const Create = lazy(() => import("features/CreateModel"));
// const Model = lazy(() => import("features/Model"));
// const CodeLibrary = lazy(() => import("features/CodeLibrary"));
// const UpgradeLibrary = lazy(() => import("features/UpgradeLibrary"));
// const ReportGenerator = lazy(() => import("features/ReportGenerator/container"));
// const LegacyParametricAnalysis = lazy(() => import("features/LegacyParametricAnalysis/container"));
// const CommunitiesDashboard = lazy(() => import("features/Dashboards/CommunitiesDashboard"));
// const Community = lazy(() => import("features/Community"));
// const QuickStart = lazy(() => import("features/Resources/QuickStart"));
// const FAQs = lazy(() => import("features/Resources/FAQs"));
// const ContactUs = lazy(() => import("features/Resources/ContactUs"));
// const Roadmap = lazy(() => import("features/Resources/Roadmap"));
// const Updates = lazy(() => import("features/Resources/Updates"));
// const Preferences = lazy(() => import("features/User/Preferences"));
// const Account = lazy(() => import("features/User/Account/container"));
// const Organization = lazy(() => import("features/User/Organization"));
// const PageNotFound = lazy(() => import("features/Navigation/PageNotFound"));
// const ParametricAnalysis = lazy(() => import("features/ParametricAnalysis"));
// const PasswordReset = lazy(() => import("features/Auth/PasswordReset"));
// const NewPassword = lazy(() => import("features/Auth/NewPassword"));

const SentryRoute = Sentry.withSentryRouting(Route);

const HIDE_FEEDBACK_PATHS = ["/login", "/register"];

const shouldHideFeedback = (pathname) =>
    HIDE_FEEDBACK_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));

export default () => {
    const { pathname = "" } = useLocation();
    const { user } = useContext(AuthContext);
    const history = useHistory();
    const registrationComplete = useSelector((state) => state.user.registrationComplete);
    const feedbackWidgetRef = useRef(null);

    const checkPath = useCallback(() => {
        if (!user && !ROUTES_WHITElIST.includes(pathname)) history.replace("/login");

        if (user && registrationComplete === false && pathname !== "/register") {
            history.replace("/register");
            return;
        }

        if (user && (pathname === "/" || pathname === "")) {
            history.replace("/dashboard");
        }
    }, [user, pathname, history, registrationComplete]);

    useEffect(() => {
        checkPath();
    }, [checkPath]);

    useEffect(() => {
        const feedback = Sentry.getFeedback();
        if (!feedback) return undefined;

        if (shouldHideFeedback(pathname)) {
            feedbackWidgetRef.current?.removeFromDom();
            feedbackWidgetRef.current = null;
            return undefined;
        }

        if (!feedbackWidgetRef.current) {
            feedbackWidgetRef.current = feedback.createWidget();
        }

        return undefined;
    }, [pathname]);

    useEffect(() => {
        return () => {
            feedbackWidgetRef.current?.removeFromDom();
            feedbackWidgetRef.current = null;
        };
    }, []);

    return (
        <Sentry.ErrorBoundary fallback={(error) => <ErrorFallback fallbackAction={() => {}} error={error} />}>
            <Suspense fallback={<Loading />}>
                <Switch>
                    <SentryRoute path="/login" exact component={Login} />
                    <SentryRoute path="/register" exact component={Register} />
                    <SentryRoute path="/password-reset" exact component={PasswordReset} />
                    <SentryRoute path="/password-reset/new-password" exact component={NewPassword} />
                    <SentryRoute path="/action-redirect" exact component={ActionRedirect} />
                    <SentryRoute path="/pricing" exact component={Pricing} />
                    <PrivateRoute path="/dashboard" component={Dashboard} />
                    <PrivateRoute path="/404-page-not-found" component={PageNotFound} />
                    <PrivateRoute path="/updates" exact component={Updates} />
                    <PrivateRoute path="/documentation/emission-factors" exact component={EmissionFactors} />
                    <PrivateRoute path="/documentation/operating-conditions" exact component={OperatingConditions} />
                    <PrivateRoute path="/documentation/reference-houses" exact component={ReferenceHouses} />
                    <PrivateRoute path="/code-library" component={CodeLibrary} />
                    <PrivateRoute path="/upgrade-library" component={UpgradeLibrary} />
                    <PrivateRoute path="/generate-report" component={ReportGenerator} />
                    <PrivateRoute path="/legacy-parametric-analysis" component={LegacyParametricAnalysis} />
                    <PrivateRoute path="/parametric-analysis" component={ParametricAnalysis} />
                    <PrivateRoute path="/communities/dashboard" component={CommunitiesDashboard} />
                    <PrivateRoute path="/communities/:communityId" component={Community} />
                    <PrivateRoute path="/resources/quickstart" exact component={QuickStart} />
                    <PrivateRoute path="/resources/faqs" exact component={FAQs} />
                    <PrivateRoute path="/resources/contact" exact component={ContactUs} />
                    <PrivateRoute path="/resources/roadmap" exact component={Roadmap} />
                    <PrivateRoute path="/resources/terms" exact component={TermsAndConditions} />
                    <PrivateRoute path="/resources/privacy" exact component={PrivacyPolicy} />
                    <PrivateRoute path="/resources/licence" exact component={SoftwareLicence} />
                    <PrivateRoute path="/user/preferences" exact component={Preferences} />
                    <PrivateRoute path="/user/account" exact component={Account} />
                    <PrivateRoute path="/user/organization/:orgId?" exact component={Organization} />
                    <PrivateRoute path="/user/transactions" exact component={Transactions} />
                    <PrivateRoute path="/create" component={Create} />
                    <PrivateRoute path="/models/:modelId" component={Model} />
                    <PrivateRoute path="/insights/:modelId" component={Insights} />
                    <PrivateRoute path="/communities/" component={CommunitiesDashboard} />
                    {/* <PrivateRoute path="/" component={Dashboard} /> */}
                    <PrivateRoute path="*" component={PageNotFound} />
                </Switch>
            </Suspense>
        </Sentry.ErrorBoundary>
    );
};
